import {
  CheckIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { useOrganization } from "../../context/OrganizationContext";
import { billingService } from "../../services/billingService";
import { BillingCycle, PlanType } from "../../types/billing";

interface PlanFeature {
  name: string;
  included: boolean;
  description?: string;
}

interface PlanDisplay {
  type: PlanType;
  name: string;
  description: string;
  monthlyBasePrice: number; // in pence
  annualBasePrice: number; // in pence
  monthlyEditorPrice: number; // in pence
  annualEditorPrice: number; // in pence
  features: PlanFeature[];
  limits: {
    maxUsers?: number;
    maxEditors?: number;
    maxPropositions?: number;
    maxSegments?: number;
  };
  recommended?: boolean;
}

// Hardcoded pricing based on issue #113
const PRICING = {
  free: {
    monthlyBase: 0,
    annualBase: 0,
    monthlyEditor: 0,
    annualEditor: 0,
  },
  starter: {
    monthlyBase: 2999, // £29.99
    annualBase: 29990, // £299.90/year
    monthlyEditor: 1499, // £14.99
    annualEditor: 14990, // £149.90/year
  },
  professional: {
    monthlyBase: 4999, // £49.99
    annualBase: 49990, // £499.90/year
    monthlyEditor: 999, // £9.99
    annualEditor: 9990, // £99.90/year
  },
};

interface PlanSelectorProps {
  currentPlan?: PlanType;
  currentEditors?: number;
  onSelectPlan?: (plan: PlanType, billingCycle: BillingCycle) => void;
  selectedPlan?: PlanType; // For create org flow to show selected plan
}

export const PlanSelector: React.FC<PlanSelectorProps> = ({
  currentPlan = "free",
  currentEditors = 1,
  onSelectPlan,
  selectedPlan,
}) => {
  const { selectedOrg } = useOrganization();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [editorCount, setEditorCount] = useState(Math.max(1, currentEditors));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanDisplay[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Fetch available plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setPlansLoading(true);
        const planConfigs = await billingService.getAvailablePlans();

        // Transform API data to display format
        const displayPlans: PlanDisplay[] = planConfigs
          .filter((config) => config.plan_type !== "enterprise") // Don't show enterprise in selector
          .map((config) => {
            const pricing =
              PRICING[config.plan_type as keyof typeof PRICING] || PRICING.free;

            // Build features list based on configuration
            const features: PlanFeature[] = [];

            // User limits
            if (config.limits.max_total_users) {
              features.push({
                name: `Up to ${config.limits.max_total_users} users`,
                included: true,
              });
            } else {
              features.push({
                name: "Unlimited users",
                included: true,
              });
            }

            // Editor limits
            if (config.plan_type === "free") {
              features.push({
                name: "1 editor seat included",
                included: true,
              });
            } else {
              features.push({
                name: "Pay per editor seat",
                included: true,
              });
            }

            // Object limits
            if (config.limits.max_propositions) {
              features.push({
                name: `${config.limits.max_propositions} propositions`,
                included: true,
              });
            } else {
              features.push({
                name: "Unlimited propositions",
                included: true,
              });
            }

            if (config.limits.max_segments) {
              features.push({
                name: `${config.limits.max_segments} segments`,
                included: true,
              });
            } else {
              features.push({
                name: "Unlimited segments",
                included: true,
              });
            }

            // Feature access
            features.push({
              name: "AI research features",
              included: config.features.genai_access,
            });

            features.push({
              name: "Basic analytics",
              included: true,
            });

            if (config.features.advanced_analytics) {
              features.push({
                name: "Advanced analytics",
                included: true,
              });
            }

            features.push({
              name: "API access",
              included: config.features.api_access,
            });

            features.push({
              name: "Priority support",
              included: config.features.priority_support,
            });

            return {
              type: config.plan_type as PlanType,
              name: config.display_name,
              description: config.description || "",
              monthlyBasePrice: pricing.monthlyBase,
              annualBasePrice: pricing.annualBase,
              monthlyEditorPrice: pricing.monthlyEditor,
              annualEditorPrice: pricing.annualEditor,
              features,
              limits: {
                maxUsers: config.limits.max_total_users,
                maxEditors: config.limits.max_editors,
                maxPropositions: config.limits.max_propositions,
                maxSegments: config.limits.max_segments,
              },
              recommended: config.plan_type === "starter",
            };
          });

        setPlans(displayPlans);
      } catch (err) {
        // console.error removed
        // Fall back to hardcoded plans if API fails
        setPlans([
          {
            type: "free",
            name: "Free",
            description: "Essential features for small teams",
            monthlyBasePrice: 0,
            annualBasePrice: 0,
            monthlyEditorPrice: 0,
            annualEditorPrice: 0,
            features: [
              { name: "Up to 3 users", included: true },
              { name: "1 editor seat included", included: true },
              { name: "1 proposition", included: true },
              { name: "3 segments", included: true },
              { name: "Basic analytics", included: true },
              { name: "AI research features", included: false },
              { name: "API access", included: false },
              { name: "Priority support", included: false },
            ],
            limits: {
              maxUsers: 3,
              maxEditors: 1,
              maxPropositions: 1,
              maxSegments: 3,
            },
          },
          {
            type: "starter",
            name: "Starter",
            description: "Perfect for growing teams",
            monthlyBasePrice: 2999,
            annualBasePrice: 29990,
            monthlyEditorPrice: 1499,
            annualEditorPrice: 14990,
            features: [
              { name: "Up to 5 users", included: true },
              { name: "3 propositions", included: true },
              { name: "3 segments", included: true },
              { name: "AI research features", included: true },
            ],
            limits: {
              maxUsers: 5,
              maxEditors: 5,
              maxPropositions: 3,
              maxSegments: 3,
            },
            recommended: true,
          },
          {
            type: "professional",
            name: "Professional",
            description: "Advanced features for larger teams",
            monthlyBasePrice: 4999,
            annualBasePrice: 49990,
            monthlyEditorPrice: 999,
            annualEditorPrice: 9990,
            features: [
              { name: "Unlimited users", included: true },
              { name: "Unlimited editor seats", included: true },
              { name: "Unlimited propositions", included: true },
              { name: "Unlimited segments", included: true },
            ],
            limits: {},
          },
        ]);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const calculatePrice = (plan: PlanDisplay): number => {
    if (plan.type === "free") return 0;

    const basePrice =
      billingCycle === "annual" ? plan.annualBasePrice : plan.monthlyBasePrice;
    const editorPrice =
      billingCycle === "annual"
        ? plan.annualEditorPrice
        : plan.monthlyEditorPrice;

    // Base price includes 1 editor, only add cost for additional editors
    const additionalEditors = Math.max(0, editorCount - 1);
    return basePrice + additionalEditors * editorPrice;
  };

  const getAnnualDiscount = (plan: PlanDisplay): number => {
    if (plan.type === "free") return 0;

    const monthlyTotal =
      (plan.monthlyBasePrice + editorCount * plan.monthlyEditorPrice) * 12;
    const annualTotal = calculatePrice(plan);

    if (monthlyTotal === 0) return 0;
    return Math.round(((monthlyTotal - annualTotal) / monthlyTotal) * 100);
  };

  const formatPrice = (price: number): string => {
    return billingService.formatPrice(price, "GBP");
  };

  const handleSelectPlan = async (planType: PlanType) => {
    // If we have a callback (like in create org flow), use it
    if (onSelectPlan) {
      setError(null);
      onSelectPlan(planType, billingCycle);
      return;
    }

    // Otherwise, do the normal upgrade flow checks
    if (!selectedOrg) {
      setError("No organization selected");
      return;
    }

    if (planType === currentPlan) {
      setError("You are already on this plan");
      return;
    }

    if (planType === "free") {
      setError("Please contact support to downgrade to the free plan");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // Default behavior - create checkout session
      const session = await billingService.createSubscription(
        selectedOrg.id,
        planType,
        billingCycle,
        editorCount,
        `${window.location.origin}/organization?tab=billing&status=success`,
        `${window.location.origin}/organization?tab=billing&status=cancelled`,
      );

      // Redirect to Stripe Checkout
      window.location.href = session.checkout_url;
    } catch (err: any) {
      // console.error removed
      const errorDetail = err.response?.data?.detail;
      const errorMessage =
        typeof errorDetail === "string"
          ? errorDetail
          : errorDetail?.msg ||
            errorDetail?.message ||
            "Failed to start checkout process";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (plansLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-200 rounded w-64 mx-auto mb-8"></div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-200 rounded-lg h-96"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Billing Cycle Toggle */}
      <div className="flex justify-center mb-8">
        <div className="relative bg-gray-100 rounded-lg p-1 flex">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`
              relative px-6 py-2 rounded-md text-sm font-medium transition-all
              ${billingCycle === "monthly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}
            `}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("annual")}
            className={`
              relative px-6 py-2 rounded-md text-sm font-medium transition-all flex items-center
              ${billingCycle === "annual" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}
            `}
          >
            <span>Annual</span>
            <span className="ml-1 text-green-600 text-xs font-semibold whitespace-nowrap">
              Save 17%
            </span>
          </button>
        </div>
      </div>

      {/* Editor Count Selector - only show for paid plans */}
      {currentPlan !== "free" && (
        <div className="flex justify-center mb-8">
          <div className="text-center">
            <label
              htmlFor="editor-count"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Number of Editor Seats (1 included in base price)
            </label>
            <div className="flex items-center justify-center space-x-3">
              <button
                onClick={() => setEditorCount(Math.max(1, editorCount - 1))}
                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                -
              </button>
              <input
                id="editor-count"
                type="number"
                min="1"
                value={editorCount}
                onChange={(e) =>
                  setEditorCount(Math.max(1, parseInt(e.target.value) || 1))
                }
                className="w-16 text-center rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
              <button
                onClick={() => setEditorCount(editorCount + 1)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                +
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Editors can create and modify content. Viewers have read-only
              access.
            </p>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {plans.map((plan) => {
          const price = calculatePrice(plan);
          const discount =
            billingCycle === "annual" ? getAnnualDiscount(plan) : 0;
          const isCurrentPlan = plan.type === currentPlan;
          const isSelectedPlan = selectedPlan
            ? plan.type === selectedPlan
            : false;

          return (
            <div
              key={plan.type}
              className={`
                relative rounded-lg border-2 p-6 shadow-sm transition-all
                ${
                  isSelectedPlan
                    ? "border-green-500 bg-green-50"
                    : plan.recommended
                      ? "border-blue-500"
                      : "border-gray-200"
                }
                ${isCurrentPlan && !isSelectedPlan ? "bg-gray-50" : isSelectedPlan ? "bg-green-50" : "bg-white"}
              `}
            >
              {/* Recommended Badge */}
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-0.5 text-sm font-medium text-blue-800">
                    <SparklesIcon className="mr-1 h-4 w-4" />
                    Recommended
                  </span>
                </div>
              )}

              {/* Current Plan Badge */}
              {isCurrentPlan && !isSelectedPlan && (
                <div className="absolute -top-3 right-4">
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-0.5 text-sm font-medium text-gray-800">
                    Current Plan
                  </span>
                </div>
              )}

              {/* Selected Plan Badge (for create org flow) */}
              {isSelectedPlan && (
                <div className="absolute -top-3 right-4">
                  <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-0.5 text-sm font-medium text-green-800">
                    Selected
                  </span>
                </div>
              )}

              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900">
                  {plan.name}
                </h3>
                <p className="mt-2 text-sm text-gray-500">{plan.description}</p>

                {/* Pricing */}
                <div className="mt-6">
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-extrabold text-gray-900">
                      {formatPrice(price)}
                    </span>
                    <span className="ml-1 text-base font-medium text-gray-500">
                      /{billingCycle === "annual" ? "year" : "month"}
                    </span>
                  </div>

                  {/* Annual Discount */}
                  {billingCycle === "annual" && discount > 0 && (
                    <p className="mt-1 text-sm text-green-600">
                      Save {discount}% with annual billing
                    </p>
                  )}

                  {/* Per Editor Price */}
                  {plan.type !== "free" && (
                    <p className="mt-2 text-sm text-gray-500">
                      +
                      {formatPrice(
                        billingCycle === "annual"
                          ? plan.annualEditorPrice
                          : plan.monthlyEditorPrice,
                      )}{" "}
                      per additional editor
                    </p>
                  )}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handleSelectPlan(plan.type)}
                  disabled={loading || (isCurrentPlan && !onSelectPlan)}
                  className={`
                    mt-6 w-full rounded-md px-4 py-2 text-sm font-medium transition-colors
                    ${
                      isSelectedPlan
                        ? "bg-green-600 text-white"
                        : isCurrentPlan && !onSelectPlan
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : plan.recommended
                            ? "bg-blue-600 text-white hover:bg-blue-700"
                            : "bg-gray-900 text-white hover:bg-gray-800"
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  {loading
                    ? "Processing..."
                    : isSelectedPlan
                      ? "Selected"
                      : isCurrentPlan && !onSelectPlan
                        ? "Current Plan"
                        : `Choose ${plan.name}`}
                </button>
              </div>

              {/* Features List */}
              <ul className="mt-8 space-y-3">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    {feature.included ? (
                      <CheckIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
                    ) : (
                      <XMarkIcon className="h-5 w-5 flex-shrink-0 text-gray-300" />
                    )}
                    <span
                      className={`ml-3 text-sm ${feature.included ? "text-gray-700" : "text-gray-400"}`}
                    >
                      {feature.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Enterprise CTA */}
      <div className="mt-12 rounded-lg bg-gray-50 p-8 text-center">
        <h3 className="text-lg font-medium text-gray-900">
          Need more? Contact us for Enterprise
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          Custom limits, dedicated support, on-premise deployment, and more
        </p>
        <button
          onClick={() =>
            (window.location.href = "mailto:sales@obviousstrategy.com")
          }
          className="mt-4 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Contact Sales
        </button>
      </div>
    </div>
  );
};
